import { z } from "zod";
import { baseEntitySchema } from "./base";
import { inventoryStatusSchema } from "./inventory";

export enum ReportType {
  PRODUCT = "product",
  MONTHLY = "monthly",
  INVENTORY_MONTHLY = "inventory_monthly",
  INVENTORY_VALUE_DAILY = "inventory_value_daily",
}

export enum ReportBuyOrSell {
  BUY = "buy",
  SELL = "sell",
  BOTH = "both",
}

// Base report data schema with common metrics
export const baseReportDataSchema = z.object({
  type: z.nativeEnum(ReportType),
  buyOrSell: z.enum([
    ReportBuyOrSell.BUY,
    ReportBuyOrSell.SELL,
    ReportBuyOrSell.BOTH,
  ]),

  // Core metrics
  totalDeals: z.number(),
  numberOfPendingDeals: z.number(),
  totalQuantity: z.number(),
  totalRevenue: z.number(),
  lostRevenue: z.number(),
  wonDeals: z.number(),
  lostDeals: z.number(),

  // Calculated fields
  winRate: z.number(), // wonDeals / totalDeals
  averageDealValue: z.number(), // totalRevenue / wonDeals
  potentialRevenue: z.number(), // totalRevenue + lostRevenue
  pendingPercentageOfDeals: z.number(), // pendingDeals / totalDeals
  wonPercentageOfTotal: z.number(), // wonDeals / totalDeals
  lostPercentageOfTotal: z.number(), // lostDeals / totalDeals
  rejectionRate: z.number(), // lostDeals / totalDeals

  // Metadata
  lastUpdated: z.date(),
  dataAsOf: z.date(),

  sourceDealIds: z.array(z.string()).optional(),
});
export type BaseReportData = z.infer<typeof baseReportDataSchema>;

export const baseReportSchema = baseEntitySchema.merge(baseReportDataSchema);
export type BaseReport = z.infer<typeof baseReportSchema>;

// Product-specific report data schema
export const productReportDataSchema = baseReportDataSchema.extend({
  productId: z.string(),
  productName: z.string(),
  boughtMiles: z.number().optional(),
  soldMiles: z.number().optional(),
});

export type ProductReportData = z.infer<typeof productReportDataSchema>;

export const productReportSchema = baseEntitySchema.merge(
  productReportDataSchema,
);
export type ProductReport = z.infer<typeof productReportSchema>;

// Monthly report data schema
export const monthlyReportDataSchema = baseReportDataSchema.extend({
  year: z.number(),
  month: z.number(), // 1-12
  monthKey: z.string(), // "2025-01" format for easy querying

  // Monthly specific metrics
  uniqueProducts: z.number(),
  uniqueCustomers: z.number(),
  topProductId: z.string().optional(),
  topProductName: z.string().optional(),
  topProductRevenue: z.number().optional(),

  boughtMiles: z.number().optional(),
  soldMiles: z.number().optional(),
});

export type MonthlyReportData = z.infer<typeof monthlyReportDataSchema>;

export const monthlyReportSchema = baseEntitySchema.merge(
  monthlyReportDataSchema,
);
export type MonthlyReport = z.infer<typeof monthlyReportSchema>;

// Inventory monthly report data schema (operational stock/throughput metrics).
// Stock value is cost basis: end-of-month miles on hand valued at the
// cumulative weighted-average buy CPM through that month.
export const inventoryMonthlyReportDataSchema = z.object({
  type: z.literal(ReportType.INVENTORY_MONTHLY),
  year: z.number(),
  month: z.number(), // 1-12
  monthKey: z.string(), // "2025-01" format

  purchaseCount: z.number(),
  salesCount: z.number(),
  milesPurchased: z.number(),
  milesSold: z.number(),
  purchaseCost: z.number(),
  salesRevenue: z.number(),

  milesOnHand: z.number(),
  avgBuyCpm: z.number(),
  endOfMonthStockValue: z.number(),

  // Supplier payment waterfall (cumulative cost basis through month end).
  // paidValue + paidOnceBookedValue + afterValue + advanceOwedValue ===
  // totalCommittedCost. Defaulted so older report docs still parse.
  totalCommittedCost: z.number().default(0),
  paidValue: z.number().default(0),
  paidOnceBookedValue: z.number().default(0),
  afterValue: z.number().default(0),
  advanceOwedValue: z.number().default(0),

  lastUpdated: z.date(),
  dataAsOf: z.date(),
});

export type InventoryMonthlyReportData = z.infer<
  typeof inventoryMonthlyReportDataSchema
>;

export const inventoryMonthlyReportSchema = baseEntitySchema.merge(
  inventoryMonthlyReportDataSchema,
);
export type InventoryMonthlyReport = z.infer<
  typeof inventoryMonthlyReportSchema
>;

// Inventory value daily snapshot. A timestamped daily record of what stock was
// worth (cost basis), so we can answer "what was our inventory worth on date X"
// instead of only a live snapshot.
//
// The supplier-payment split reads each PO's CURRENT amountPaid (not versioned),
// so it is only trustworthy for snapshots recorded live going forward
// (paymentSplitAvailable === true). Backfilled historical days reconstruct value
// only and leave paymentSplitAvailable === false with the split fields at 0.
export const inventoryValueByStatusSchema = z.object({
  status: inventoryStatusSchema,
  miles: z.number(),
  stockValueLeft: z.number(),
});
export type InventoryValueByStatus = z.infer<
  typeof inventoryValueByStatusSchema
>;

export const inventoryValueSnapshotDataSchema = z.object({
  type: z.literal(ReportType.INVENTORY_VALUE_DAILY),
  date: z.string(), // "YYYY-MM-DD"

  milesOnHand: z.number(),
  avgBuyCpm: z.number(),
  stockValueLeft: z.number(),
  totalCommittedCost: z.number(),

  paymentSplitAvailable: z.boolean().default(false),
  paidValue: z.number().default(0),
  paidOnceBookedValue: z.number().default(0),
  afterValue: z.number().default(0),
  advanceOwedValue: z.number().default(0),

  byStatus: z.array(inventoryValueByStatusSchema).optional(),

  lastUpdated: z.date(),
  dataAsOf: z.date(),
});

export type InventoryValueSnapshotData = z.infer<
  typeof inventoryValueSnapshotDataSchema
>;

export const inventoryValueSnapshotSchema = baseEntitySchema.merge(
  inventoryValueSnapshotDataSchema,
);
export type InventoryValueSnapshot = z.infer<
  typeof inventoryValueSnapshotSchema
>;

// Union types for convenience.
//
// NOTE: these unions intentionally cover only the deal-pipeline report types,
// which the existing reports UI (ReportTable, QuickStats, DealsSheet) treats as
// a closed set. The operational InventoryMonthlyReport is read through its own
// hook (useInventoryMonthlyReports) and is deliberately kept out of these
// unions so it doesn't force narrowing across all deal-report consumers.
export type ReportData = ProductReportData | MonthlyReportData;
export type Report = ProductReport | MonthlyReport;
